import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Building2, Mail, Lock, Phone, MapPin, BadgeCheck, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import ShiftsLayout from "../../components/shifts/ShiftsLayout";
import SuccessState from "../../components/shifts/SuccessState";
import SMSConsentBlock from "../../components/SMSConsentBlock";
import PageMeta from "../../components/seo/PageMeta";
import { US_STATES, FACILITY_TYPES, SHIFTS_CTAS } from "../../data/marketplaceData";
import { useAuth } from "../../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FacilitySignup() {
  const navigate = useNavigate();
  const { setSession } = useAuth();
  const [params] = useSearchParams();
  const intent = params.get("intent");

  const [form, setForm] = useState({
    facility_name: "", facility_type: "Acute Care Hospital", city: "", state: "TX",
    name: "", email: "", phone: "", password: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [smsOptIn, setSmsOptIn] = useState(false); // TCPA: SMS consent

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (form.password.length < 8) { toast.error("Password must be at least 8 characters."); return; }
    setSubmitting(true);
    try {
      const requestId = (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `req-${Date.now()}`;
      const r = await fetch(`/api/shiftbridge-signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Idempotency-Key": requestId },
        body: JSON.stringify({
          role: "facility",
          name: form.name || `${form.firstName || ""} ${form.lastName || ""}`.trim(),
          email: form.email,
          phone: form.phone || null,
          facility: form.facility || form.organization || null,
          facilityType: form.facility_type || form.facilityType || null,
          location: form.location || null,
          sms_opt_in: smsOptIn,
          notes: form.notes || null,
          idempotencyKey: requestId,
        }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok || data.ok === false) throw new Error("Signup failed");
      toast.success("Signup received.", { description: "A TRITAL partnership manager will reach out within one business day." });
      setDone(true);
    } catch {
      toast.error("We couldn't complete your signup", { description: "Please try again, or email sales@tritalcare.com." });
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <ShiftsLayout>
        <PageMeta title="Facility account created — TRITAL ShiftBridge™" canonical="https://www.tritalcare.com/shiftbridge/signup/facility" noIndex />
        <section className="max-w-[1400px] mx-auto px-5 lg:px-8 py-20 lg:py-32">
          <SuccessState
            title="Account created. Approval pending."
            message="Our compliance team typically approves new facility accounts within 1 business day. We'll email you the moment your account is live so you can post your first shift."
            primary={{ label: intent === "post-shift" ? "Open dashboard" : "Browse the marketplace", to: intent === "post-shift" ? "/dashboard" : SHIFTS_CTAS.browse }}
            secondary={{ label: "Read the FAQ", to: SHIFTS_CTAS.faq }}
            testid="facility-signup-success"
          />
        </section>
      </ShiftsLayout>
    );
  }

  return (
    <ShiftsLayout>
      <PageMeta
        title="Facility signup — TRITAL ShiftBridge™ by TRITAL Care®"
        description="Create a facility account on TRITAL ShiftBridge™ to post PRN shifts, set credentialing rules, and access the TRITAL Care® workforce marketplace."
        canonical="https://www.tritalcare.com/shiftbridge/signup/facility"
        noIndex
      />
      <section className="max-w-[1100px] mx-auto px-5 lg:px-8 py-14 lg:py-20">
        <Link to="/shifts" className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-slate-500 hover:text-trital-navy mb-8" data-testid="facility-back">
          <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.8} /> Back to TRITAL ShiftBridge™
        </Link>

        <div className="grid lg:grid-cols-[1.05fr_1fr] gap-10 items-start">
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="rounded-3xl bg-white border border-slate-200 shadow-navy p-7 lg:p-9"
            data-testid="facility-signup-card"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-trital-medical-blue-soft text-trital-navy">
              <Building2 className="w-3.5 h-3.5" strokeWidth={1.8} />
              <span className="overline">Facility signup</span>
            </div>
            <h1 className="mt-4 font-display font-black text-3xl lg:text-4xl text-slate-900 text-balance">Post your first shift in 90 seconds.</h1>
            <p className="mt-3 text-[14px] text-slate-600">We approve new facility accounts within 1 business day. No setup fees.</p>

            <form onSubmit={submit} className="mt-7 space-y-4" data-testid="facility-signup-form">
              <Field label="Facility / organization name" required>
                <input type="text" required value={form.facility_name} onChange={update("facility_name")} placeholder="Houston Medical Center" data-testid="facility-name" className={inputCls} />
              </Field>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Facility type" required>
                  <select required value={form.facility_type} onChange={update("facility_type")} data-testid="facility-type" className={inputCls}>
                    {FACILITY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </Field>
                <Field label="State" required>
                  <select required value={form.state} onChange={update("state")} data-testid="facility-state" className={inputCls}>
                    {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </Field>
              </div>
              <Field label="City" required>
                <input type="text" required value={form.city} onChange={update("city")} placeholder="Houston" data-testid="facility-city" className={inputCls} />
              </Field>

              <div className="h-px bg-slate-100 my-2" />
              <div className="text-[11px] uppercase tracking-[0.16em] font-bold text-slate-500">Primary scheduler / DON</div>

              <Field label="Full name" required>
                <input type="text" required value={form.name} onChange={update("name")} placeholder="Maya Patel, RN, BSN" data-testid="facility-contact-name" className={inputCls} />
              </Field>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Work email" required>
                  <input type="email" required value={form.email} onChange={update("email")} placeholder="m.patel@hospital.org" data-testid="facility-email" className={inputCls} />
                </Field>
                <Field label="Phone" required>
                  <input type="tel" required value={form.phone} onChange={update("phone")} placeholder="(713) 555-0142" data-testid="facility-phone" className={inputCls} />
                </Field>
              </div>
              <Field label="Choose a password (min 8 chars)" required>
                <input type="password" required minLength={8} value={form.password} onChange={update("password")} placeholder="••••••••" data-testid="facility-password" className={inputCls} />
              </Field>

              {/* TCPA: SMS Consent Block — ALWAYS visible (SimpleTexting requirement) */}
              <div className="my-4">
                <SMSConsentBlock
                  checked={smsOptIn}
                  onCheckedChange={setSmsOptIn}
                  checkboxId="facility-signup-sms-consent"
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-full bg-trital-navy text-white text-[14px] font-semibold btn-lift hover:bg-trital-navy-deep disabled:opacity-70 disabled:cursor-not-allowed"
                data-testid="facility-submit"
              >
                {submitting ? "Creating account…" : <>Create facility account <ArrowRight className="w-4 h-4" strokeWidth={1.8} /></>}
              </button>
              <div className="text-[12px] text-slate-500 text-center">
                Already have an account? <Link to={SHIFTS_CTAS.login} className="font-semibold text-trital-navy hover:underline">Sign in</Link>
              </div>
            </form>
          </motion.div>

          {/* Reassurance side panel */}
          <aside className="space-y-3">
            {[
              { Icon: BadgeCheck, t: "1 business day approval", b: "Our compliance team verifies your facility license + NPI and turns the account live within 24 hours." },
              { Icon: Lock,       t: "SOC 2 controls", b: "All shift data is encrypted at rest and in transit. Account-manager + clinical liaison assigned at signup." },
              { Icon: Phone,      t: "Talk to a human first", b: "Prefer to be walked through it? Call (832) 303-6622 or email sales@tritalcare.com." },
            ].map((c) => (
              <div key={c.t} className="rounded-2xl bg-slate-50 border border-slate-200 p-5">
                <c.Icon className="w-5 h-5 text-trital-red" strokeWidth={1.8} />
                <div className="mt-3 font-display font-bold text-[15px] text-slate-900">{c.t}</div>
                <p className="mt-1.5 text-[13px] text-slate-600 leading-relaxed">{c.b}</p>
              </div>
            ))}
          </aside>
        </div>
      </section>
    </ShiftsLayout>
  );
}

const inputCls = "w-full px-4 py-3 rounded-xl border border-slate-200 bg-white text-[14px] focus:outline-none focus:border-trital-navy focus:ring-2 focus:ring-trital-navy/10 transition";

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="text-[12px] font-semibold text-slate-700 mb-1.5 inline-block">
        {label} {required && <span className="text-trital-red">*</span>}
      </span>
      {children}
    </label>
  );
}
